import { useEffect, useState } from "react";

export type ConsentCategory = "analytics" | "marketing";

/** Granular consent state. Strictly necessary cookies are always active. */
export type ConsentState = {
  analytics: boolean;
  marketing: boolean;
};

export const DENY_ALL: ConsentState = { analytics: false, marketing: false };
export const ALLOW_ALL: ConsentState = { analytics: true, marketing: true };

/**
 * Bump this whenever the tracking logic changes (new vendor, new category,
 * different data collected). A stored decision from an older version is
 * treated as "no decision", so the banner asks again.
 */
export const CONSENT_VERSION = 2;

/** Consent expires after 180 days, then the visitor is asked again. */
export const CONSENT_TTL_DAYS = 180;
const TTL_MS = CONSENT_TTL_DAYS * 24 * 60 * 60 * 1000;

const STORAGE_KEY = "oxory-cookie-consent";
const EVENT = "oxory:consent-change";

/** Persisted record: the decision plus the version and time it was given. */
export type ConsentRecord = ConsentState & {
  version: number;
  /** ISO timestamp of when the decision was given. */
  decidedAt: string;
};

export type ConsentInvalidReason = "outdated" | "expired";

function toRecord(state: ConsentState, decidedAt = new Date()): ConsentRecord {
  return { ...state, version: CONSENT_VERSION, decidedAt: decidedAt.toISOString() };
}

function parse(raw: string | null): ConsentRecord | null {
  if (!raw) return null;
  // Legacy formats predate versioning → force a fresh decision.
  if (raw === "granted" || raw === "denied") return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentRecord>;
    if (typeof parsed !== "object" || parsed === null) return null;
    const decidedAt = typeof parsed.decidedAt === "string" ? parsed.decidedAt : null;
    if (!decidedAt || Number.isNaN(Date.parse(decidedAt))) return null;
    return {
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
      version: typeof parsed.version === "number" ? parsed.version : 0,
      decidedAt,
    };
  } catch {
    return null;
  }
}

export function isExpired(record: ConsentRecord, now = Date.now()): boolean {
  return now - Date.parse(record.decidedAt) > TTL_MS;
}

/** Why a stored decision is no longer valid, or null when it still counts. */
export function invalidReason(
  record: ConsentRecord | null,
  now = Date.now(),
): ConsentInvalidReason | null {
  if (!record) return null;
  if (record.version !== CONSENT_VERSION) return "outdated";
  if (isExpired(record, now)) return "expired";
  return null;
}

/**
 * Reads the raw stored record, including outdated or expired ones. Use
 * `readConsent()` for the effective decision.
 */
export function readConsentRecord(): ConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    return parse(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * Reads the stored consent. Returns null when the user hasn't decided yet, or
 * when the decision was given for an older tracking version / has expired.
 */
export function readConsent(): ConsentState | null {
  const record = readConsentRecord();
  if (!record || invalidReason(record)) return null;
  return { analytics: record.analytics, marketing: record.marketing };
}

export function writeConsent(state: ConsentState) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(toRecord(state)));
  } catch {
    /* storage unavailable — consent stays session-only */
  }
  window.dispatchEvent(new CustomEvent<ConsentState | null>(EVENT, { detail: state }));
}

/**
 * Withdraws the decision completely: stored consent is deleted and the banner
 * shows again. Listeners (telemetry, marketing tags) react instantly.
 */
export function resetConsent() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent<ConsentState | null>(EVENT, { detail: null }));
}

/** Expiry date of a stored decision, for display in the settings modal. */
export function expiresAt(record: ConsentRecord): Date {
  return new Date(Date.parse(record.decidedAt) + TTL_MS);
}


/**
 * Client-side consent state. `undefined` while hydrating so nothing renders
 * server-side and nothing loads before the user has opted in.
 */
export function useConsent(): ConsentState | null | undefined {
  const [consent, setConsent] = useState<ConsentState | null | undefined>(undefined);

  useEffect(() => {
    setConsent(readConsent());
    const onChange = (event: Event) =>
      setConsent((event as CustomEvent<ConsentState | null>).detail ?? null);
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, []);

  return consent;
}

/**
 * Tells why the banner is showing again: "outdated" after a tracking-logic
 * change (CONSENT_VERSION bump) or "expired" after CONSENT_TTL_DAYS.
 */
export function useConsentRenewal(): ConsentInvalidReason | null {
  const [reason, setReason] = useState<ConsentInvalidReason | null>(null);

  useEffect(() => {
    setReason(invalidReason(readConsentRecord()));
    const onChange = () => setReason(invalidReason(readConsentRecord()));
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, []);

  return reason;
}

/** The stored record (may be outdated/expired), kept in sync with changes. */
export function useConsentRecord(): ConsentRecord | null {
  const [record, setRecord] = useState<ConsentRecord | null>(null);

  useEffect(() => {
    setRecord(readConsentRecord());
    const onChange = () => setRecord(readConsentRecord());
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, []);

  return record;
}

/** True only when the visitor explicitly opted into that category. */
export function useConsentFor(category: ConsentCategory): boolean {
  const consent = useConsent();
  return consent ? consent[category] === true : false;
}


const OPEN_EVENT = "oxory:consent-open";

/** Opens the cookie settings modal from anywhere (footer link, banner link). */
export function openConsentSettings() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/** Subscribes to cookie-settings open requests. */
export function useConsentSettings() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  return { open, setOpen } as const;
}
